const EQUALS = "equals";
const NOT_EQUALS = "not equals";
const CONTAINS = "contains";
const MISSING = "missing";
const ANY = "ANY";
const PIPE = "|";
const GLOBAL = "g";

enum ConditionOperators {
    AND = "and",
    OR = "or",
}

const operatorRegex = new RegExp(`(${EQUALS}${PIPE}${NOT_EQUALS}${PIPE}${CONTAINS}${PIPE}${MISSING})`);
const expressionOperatorsRegexString = `( ${EQUALS} ${PIPE} ${NOT_EQUALS} ${PIPE} ${CONTAINS} ${PIPE} ${MISSING} )`;
const operatorsBetweenExpressionsRegexString = `( ${ConditionOperators.AND} ${PIPE} ${ConditionOperators.OR} )`;

type ResolveMethod = (fieldValue: ValueType, valueString: string, value: ValueType) => boolean;
type ResolveMap = { [op: string]: ResolveMethod };

const Resolvers: ResolveMap = {
    [EQUALS]: resolveEqual,
    [NOT_EQUALS]: resolveNotEqual,
    [CONTAINS]: resolveContains,
    [MISSING]: resolveMissing,
};

export function resolveCondition(expressions: string[], operators: ConditionOperators[], fieldValuesMap: object) {
    const expressionsResults = expressions.map((exp) => resolveExpressionToBoolean(exp, fieldValuesMap));
    const operatorsMap = {
        and: "&&",
        or: "||",
    };
    const conditionStringResolved = expressionsResults.reduce((acc, next, idx) => {
        const operator = operators[idx];
        return `${acc} ${next}${operator ? ` ${operatorsMap[operator]}` : ""}`;
    }, "");
    return conditionStringResolved.trim();
}

export function getExpressionsFromCondition(watchedFields: string[], condition: string) {
    const fieldNamesRegexString = `(${watchedFields.join(PIPE)})`;
    const regexString = String.raw`${fieldNamesRegexString}${expressionOperatorsRegexString}[^\s]+`;

    const expressions = condition.match(new RegExp(regexString, GLOBAL)) || [];
    const operators = (condition.match(new RegExp(operatorsBetweenExpressionsRegexString, GLOBAL))?.map((str) => str.trim()) ||
        []) as ConditionOperators[];

    return { expressions, operators };
}

function resolveExpressionToBoolean(expression: string, fieldValuesMap: object): boolean {
    const expressionParts = expression.split(operatorRegex);

    const fieldName = expressionParts[0].trim();
    if (!fieldValuesMap || !Object.hasOwn(fieldValuesMap, fieldName)) {
        return false;
    }

    // @ts-ignore
    const fieldValue = fieldValuesMap[fieldName];

    const valueString = expressionParts[2].trim();
    // we want to convert string value to boolean in case the value is `true` or `false` as strings
    // eslint-disable-next-line
    const value: string | boolean = valueString === "true" || valueString === "false"? eval(valueString) : valueString;

    const expressionOperator = expression.match(operatorRegex)?.[0] as string;
    if (!expressionOperator) {
        throw Error("The expression has not resolved to a boolean");
    }

    const resolve = Resolvers[expressionOperator];
    if (!resolve) {
        throw Error("The expression has not resolved to a boolean");
    }

    return resolve(fieldValue, valueString, value);
}

type ValueType = string | boolean | string[];

function resolveEqual(fieldValue: ValueType, valueString: string, value: ValueType) {
    if (!fieldValue) {
        return false;
    }
    // Special case that checks if the field has set any value
    if (valueString === ANY) {
        return !!fieldValue;
    }

    // @ts-ignore
    return fieldValue === value;
}

function resolveNotEqual(fieldValue: ValueType, valueString: string, value: ValueType) {
    if (!fieldValue) {
        return true;
    }
    // Special case that checks if the field has not set any value
    if (valueString === ANY) {
        return !fieldValue;
    }

    // @ts-ignore
    return fieldValue !== value;
}

function resolveContains(fieldValue: ValueType, valueString: string, value: ValueType) {
    const isArray = Array.isArray(fieldValue);
    if (!fieldValue || !isArray) {
        return false;
    }
    // Special case that checks if the field has set at least one element
    if (valueString === ANY) {
        return fieldValue.length > 0;
    }

    return fieldValue.length > 0 && fieldValue.some((v: string) => v === value);
}

function resolveMissing(fieldValue: ValueType, valueString: string, value: ValueType) {
    const hasValue = !!fieldValue;
    const isArray = hasValue && Array.isArray(fieldValue);
    const isEmptyArray = isArray && fieldValue.length === 0;
    if (!hasValue || !isArray || isEmptyArray) {
        return true;
    }
    // Special case that checks if the field is empty
    if (valueString === ANY) {
        return fieldValue.length === 0;
    }

    return fieldValue.length !== 0 && fieldValue.filter((v: string) => v === value).length === 0;
}
